import posthogJs from "posthog-js"
import { useCallback } from "react"

const getBaseProps = () => ({
  session_id: window.__SESSION_ID__,
  platform: window.__PLATFORM__, // web / ios / android / tv
  app_version: window.__APP_VERSION__,
  network_type: navigator.connection?.effectiveType || 'unknown',
  ts: Date.now(),
})

export const usePostHogEvents = () => {
  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      captureEvent(eventName, properties)
    },
    []
  )

  // Expose posthog instance when available on client, else null
  const ph = typeof window !== 'undefined' ? posthogJs : null
  return { trackEvent, posthog: ph }
}

// Global (non-React) capture helper for utility code
export const captureEvent = (eventName: string, properties?: Record<string, any>) => {
  if (!posthogJs) return
  try {
    posthogJs.capture(eventName, {
      ...getBaseProps(),
      ...properties,
    })
  } catch {
    // swallow to avoid cross-cutting failures in network/util layers
  }
}

export const EVENTS = {
  /* ===================== VIDEO PLAYBACK ===================== */
  VIDEO_PLAY_STARTED: 'video_play_started',
  VIDEO_PAUSED: 'video_paused',
  VIDEO_RESUMED: 'video_resumed',
  VIDEO_COMPLETED: 'video_completed',
  VIDEO_EXITED: 'video_exited',                 // drop-off backbone
  VIDEO_PROGRESS: 'video_progress',             // watch time backbone
  VIDEO_SEEKED: 'video_seeked',

  VIDEO_BUFFERING_STARTED: 'video_buffering_started',
  VIDEO_BUFFERING_ENDED: 'video_buffering_ended',

  VIDEO_QUALITY_CHANGED: 'video_quality_changed',
  VIDEO_PLAYBACK_RATE_CHANGED: 'video_playback_rate_changed',
  VIDEO_VOLUME_CHANGED: 'video_volume_changed',
  VIDEO_FULLSCREEN_ENTERED: 'video_fullscreen_entered',
  VIDEO_FULLSCREEN_EXITED: 'video_fullscreen_exited',

  VIDEO_ERROR: 'video_error',

  /* ===================== VIDEO ENGAGEMENT ===================== */
  VIDEO_LIKED: 'video_liked',
  VIDEO_UNLIKED: 'video_unliked',
  VIDEO_COMMENTED: 'video_commented',
  VIDEO_SHARED: 'video_shared',
  VIDEO_WATCHLIST_ADDED: 'video_watchlist_added',
  VIDEO_WATCHLIST_REMOVED: 'video_watchlist_removed',

  /* ===================== CREATOR ===================== */
  CREATOR_VIDEO_UPLOADED: 'creator_video_uploaded',
  CREATOR_PROFILE_UPDATED: 'creator_profile_updated',
  CREATOR_PAYOUT_PROCESSED: 'creator_payout_processed',
  CREATOR_SUBSCRIBED: 'creator_subscribed',
  CREATOR_UNSUBSCRIBED: 'creator_unsubscribed',

  /* ===================== AD EVENTS ===================== */
  AD_REQUESTED: 'ad_requested',
  AD_STARTED: 'ad_started',
  AD_FIRST_QUARTILE: 'ad_first_quartile',
  AD_MIDPOINT: 'ad_midpoint',
  AD_THIRD_QUARTILE: 'ad_third_quartile',
  AD_COMPLETED: 'ad_completed',
  AD_SKIPPED: 'ad_skipped',
  AD_CLICKED: 'ad_clicked',
  AD_VIEWABLE_IMPRESSION: 'ad_viewable_impression',

  /* ===================== USER LIFECYCLE ===================== */
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  USER_BECAME_DORMANT: 'user_became_dormant',

  /* ===================== PERFORMANCE / QoE ===================== */
  PLAYER_FRAME_DROPPED: 'player_frame_dropped',
  PLAYER_MEMORY_USAGE_REPORTED: 'player_memory_usage_reported',
  PLAYER_BITRATE_CHANGED: 'player_bitrate_changed',
  PLAYER_SEEK_LATENCY_RECORDED: 'player_seek_latency_recorded',

  /* ===================== NETWORK / ERRORS ===================== */
  ERROR_NETWORK_REQUEST: 'error_network_request',
  ERROR_API_RESPONSE: 'error_api_response',

  /* ===================== BUSINESS / SYSTEM ===================== */
  CONTENT_MODERATION_FLAGGED: 'content_moderation_flagged',
  GDPR_DELETION_REQUESTED: 'gdpr_deletion_requested',
  SEARCH_VIDEO: 'search_video',
} as const